import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Lock, Unlock, CheckCircle2, XCircle, GraduationCap, UserCheck, Pencil, Clock3,
  ShieldCheck, Scale, Target, EyeOff, History, Presentation, Ban,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import SearchSelect from './SearchSelect';
import DemoComments from './DemoComments';
import {
  TRAINER_FB, COUNSELLOR_FB, NO_ADMISSION_REASONS, NOT_DONE_REASONS, ALIGN_META, OUTCOME_META, DEMO_TYPES,
  isMgr, isAdmin, demoBase, fmtDate, fmtTime,
} from './demoConstants';
import { Card } from '../../../components/ui/card';
import { PageHeader, StatusPill, AiVerdictBanner, QuoteBlock } from './DemoUI';

const SectionCard = ({ icon: Icon, title, accent = 'violet', right, children, testid }) => (
  <Card className="p-5 border space-y-4" data-testid={testid}>
    <header className="flex items-center justify-between gap-2">
      <h2 className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2">
        {Icon && <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === 'indigo' ? 'bg-indigo-100 text-indigo-700' : accent === 'slate' ? 'bg-slate-100 text-slate-700' : 'bg-violet-100 text-violet-700'}`}><Icon className="w-4 h-4" /></span>}
        {title}
      </h2>
      {right}
    </header>
    {children}
  </Card>
);

const Pick = ({ cfg, value, onChange, testid, invalid }) => (
  <div className="space-y-1">
    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{cfg.label}<span className="text-rose-500 ml-0.5">*</span></Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className={`h-9 bg-white text-[13px] ${invalid ? 'border-rose-300 ring-1 ring-rose-200' : ''}`} data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white">{cfg.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
    {cfg.hint && <p className="text-[10.5px] text-slate-400">{cfg.hint}</p>}
  </div>
);

const ReadRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-[11.5px] text-slate-500">{label}</span>
    <span className="text-[12.5px] font-semibold text-slate-800">{value || '—'}</span>
  </div>
);

function FeedbackForm({ schema, state, setState, onSave, busy, accent, testPrefix }) {
  const [touched, setTouched] = useState(false);
  const missing = Object.keys(schema).filter((k) => !state[k]);
  const commentsShort = (state.comments || '').trim().length < 10;
  const save = () => {
    setTouched(true);
    if (missing.length || commentsShort) { toast.error('All fields and a comment (min 10 characters) are required'); return; }
    onSave();
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3.5">
        {Object.entries(schema).map(([k, cfg]) => (
          <Pick key={k} cfg={cfg} value={state[k]} onChange={(v) => setState((p) => ({ ...p, [k]: v }))} testid={`${testPrefix}-${k}`} invalid={touched && !state[k]} />
        ))}
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Comments<span className="text-rose-500 ml-0.5">*</span></Label>
        <Textarea
          rows={3}
          value={state.comments || ''}
          onChange={(e) => setState((p) => ({ ...p, comments: e.target.value }))}
          placeholder="What actually happened in the demo? Be specific — this feeds the AI alignment verdict."
          className={`text-[13px] bg-white ${touched && commentsShort ? 'border-rose-300 ring-1 ring-rose-200' : ''}`}
          data-testid={`${testPrefix}-comments`}
        />
        <p className={`text-[10.5px] ${touched && commentsShort ? 'text-rose-500' : 'text-slate-400'}`}>Mandatory · minimum 10 characters ({(state.comments || '').trim().length})</p>
      </div>
      <Button onClick={save} disabled={busy} className={`w-full ${accent === 'indigo' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-violet-600 hover:bg-violet-700'} text-white`} data-testid={`save-${testPrefix}`}>
        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</> : 'Submit Feedback'}
      </Button>
    </div>
  );
}

function FeedbackReadOnly({ schema, data, role, accent }) {
  if (!data) return <p className="text-[12.5px] text-slate-400">Not submitted yet.</p>;
  return (
    <div className="space-y-3">
      <div>{Object.entries(schema).map(([k, cfg]) => <ReadRow key={k} label={cfg.label} value={data[k]} />)}</div>
      {data.comments && <QuoteBlock text={data.comments} author={data.submitted_by} role={role} at={data.submitted_at} tone={accent} testid={`${role.toLowerCase()}-comment-block`} />}
    </div>
  );
}

export function DemoDetailBody({ demoId, forcedRo = false, onClose, onChanged, inModal = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const admin = isAdmin(role);

  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [tf, setTf] = useState({});
  const [cf, setCf] = useState({});
  const [outcome, setOutcome] = useState({ result: '', reasons: [], comments: '', follow_up_date: '', follow_up_status: '', enrollment_no: '' });
  const [reopen, setReopen] = useState('');
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [markOpen, setMarkOpen] = useState('');
  const [markCode, setMarkCode] = useState('');
  const [markNote, setMarkNote] = useState('');
  const [editing, setEditing] = useState({ tf: false, cf: false, oc: false });
  const [edit, setEdit] = useState(null);
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });

  const load = useCallback(() => {
    api.get(`/training/demos/${demoId}`).then((r) => {
      setD(r.data);
      setTf(r.data.trainer_feedback || {});
      setCf(r.data.counsellor_feedback || {});
      if (r.data.outcome) setOutcome({ reasons: [], comments: '', follow_up_date: '', follow_up_status: '', enrollment_no: '', ...r.data.outcome });
    }).catch((e) => { toast.error(e.response?.data?.detail || 'Failed to load demo'); onClose?.(); });
  }, [demoId, onClose]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async () => {
    if (!masters.trainers.length) {
      try {
        const r = await api.get('/training/demos/masters/all');
        setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] });
      } catch { /* pickers fall back to empty */ }
    }
    setEdit({
      trainer_id: d.trainer_id, counsellor_id: d.counsellor_id, course_id: d.course_id,
      demo_date: d.demo_date || '', demo_time: d.demo_time || '', demo_type: d.demo_type || '',
      student_name: d.student_name || '', notes: d.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!edit.demo_date || !edit.demo_time || !edit.demo_type) { toast.error('Date, time and demo type are required'); return; }
    setBusy(true);
    try {
      const r = await api.put(`/training/demos/${demoId}`, edit);
      toast.success(r.data.changed?.length ? 'Demo updated' : 'Nothing to update');
      setEdit(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update demo'); }
    finally { setBusy(false); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setEditing({ tf: false, cf: false, oc: false }); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading demo…</div>;

  const locked = !!d.finalised;
  const readOnly = !!d.read_only || forcedRo || !!d.void;
  const trainerFocus = role === 'trainer' && !locked;   // trainer sees only their own feedback until finalised
  const approved = d.status === 'approved';
  const canTrainerFb = !readOnly && !locked && approved && (role === 'trainer' || mgr);
  const canCounsellorFb = !readOnly && !locked && approved && (role === 'counsellor' || mgr);
  const canOutcome = !readOnly && !locked && approved && (role === 'counsellor' || mgr);
  const canFinalise = !readOnly && !locked && approved && d.trainer_feedback && d.counsellor_feedback && d.outcome;
  const align = d.alignment || {};
  const showTfForm = canTrainerFb && (!d.trainer_feedback || editing.tf);
  const showCfForm = canCounsellorFb && (!d.counsellor_feedback || editing.cf);
  const showOcForm = canOutcome && (!d.outcome || editing.oc);

  const EditBtn = ({ onClick, testid }) => (
    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onClick} data-testid={testid}>
      <Pencil className="w-3 h-3 mr-1" />Edit
    </Button>
  );
  const SubmittedTag = ({ at, onEdit, testid }) => (
    <span className="inline-flex items-center gap-2">
      <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Submitted{at ? ` · ${new Date(at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : ''}</span>
      {onEdit && <EditBtn onClick={onEdit} testid={testid} />}
    </span>
  );

  return (
    <div className={inModal ? 'space-y-5' : 'space-y-5 max-w-6xl'} data-testid="demo-detail-page">
      <PageHeader
        icon={Presentation}
        eyebrow={d.demo_code}
        title={d.course_name}
        subtitle={`${fmtDate(d.demo_date)} · ${fmtTime(d.demo_time)} · ${d.demo_type}${d.student_name ? ` · ${d.student_name}` : ''}`}
        testid="demo-detail-header"
      >
        <StatusPill status={d.status} finalised={locked} voided={!!d.void} />
        {mgr && !forcedRo && (
          <Button variant="outline" size="sm" className="h-10" onClick={openEdit} data-testid="edit-demo-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit details</Button>
        )}
        {mgr && !forcedRo && !d.void && (
          <Button variant="outline" size="sm" className="h-10 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setVoidOpen(true)} data-testid="void-demo-btn">
            <Ban className="w-4 h-4 mr-1.5" />Mark void
          </Button>
        )}
        {!inModal && <Button variant="outline" size="sm" className="h-10" onClick={() => onClose?.()} data-testid="back-to-queue"><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>}
      </PageHeader>

      {d.void && (
        <div className="rounded-xl border border-slate-300 bg-slate-100 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="void-banner">
          <p className="text-[13px] text-slate-700 flex items-start gap-2">
            <Ban className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <b>This demo is VOID</b> and is excluded from every count, conversion number and incentive total.
              {d.void_reason ? <span className="block text-[12px] text-slate-600 mt-0.5">Reason: “{d.void_reason}” — {d.voided_by}{d.voided_at ? `, ${new Date(d.voided_at).toLocaleString('en-IN')}` : ''}</span> : null}
            </span>
          </p>
          {mgr && !forcedRo && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post(`/training/demos/${demoId}/unvoid`, { reason: 'restored by manager' }), 'Demo restored')} data-testid="unvoid-btn">
              <Unlock className="w-3.5 h-3.5 mr-1.5" />Restore demo
            </Button>
          )}
        </div>
      )}

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Ban className="w-4 h-4 text-rose-600" />Mark this demo void</DialogTitle>
            <DialogDescription className="text-[11.5px] text-slate-500">
              {d.demo_code} will stop counting in every KPI, conversion number and incentive total. Nothing is deleted — the record stays auditable and can be restored.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[12px] text-slate-600">Reason <span className="text-rose-500">*</span></Label>
            <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={3}
              placeholder="e.g. duplicate entry — the same prospect was logged twice" className="text-[13px]" data-testid="void-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)} data-testid="void-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || voidReason.trim().length < 5}
              onClick={() => act(async () => { await api.post(`/training/demos/${demoId}/void`, { reason: voidReason }); setVoidOpen(false); setVoidReason(''); }, 'Demo marked void')}
              data-testid="void-confirm">Mark void</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {locked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/90 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="locked-banner">
          <p className="text-[13px] text-amber-900 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span><b>Finalised</b>{d.finalised_at ? ` on ${new Date(d.finalised_at).toLocaleString('en-IN')}` : ''}{d.auto_finalised ? ' automatically — the admission linked to the student record' : ''} — {mgr && !forcedRo ? 'you can unfreeze it to make changes.' : 'only an admin or manager can unfreeze or edit this record.'}</span>
          </p>
          {mgr && !forcedRo && (
            <div className="flex items-center gap-2">
              <Input value={reopen} onChange={(e) => setReopen(e.target.value)} placeholder="Reason to unfreeze" className="h-9 w-48 bg-white text-[12px]" data-testid="reopen-reason" />
              <Button size="sm" disabled={busy || !reopen.trim()} onClick={() => act(() => api.post(`/training/demos/${demoId}/reopen`, { reason: reopen }), 'Demo unfrozen')} className="bg-slate-900 hover:bg-slate-800" data-testid="reopen-btn">
                <Unlock className="w-3.5 h-3.5 mr-1.5" />Unfreeze
              </Button>
            </div>
          )}
        </div>
      )}

      {['not_done', 'not_applicable'].includes(d.status) && (
        <div className="rounded-xl border border-slate-300 bg-slate-100/80 p-4 flex items-start gap-2.5" data-testid="not-conducted-banner">
          <Ban className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-slate-700">
            <b>{d.status === 'not_applicable' ? 'This demo was marked not applicable' : 'This demo was not conducted'}</b>
            {d.not_conducted ? (
              <>
                {' '}— <b>{d.not_conducted.reason}</b>.
                <span className="block text-[11.5px] text-slate-500 mt-0.5">
                  Marked by {d.not_conducted.by} ({d.not_conducted.by_role}) on {new Date(d.not_conducted.at).toLocaleString('en-IN')}
                </span>
              </>
            ) : <span className="block text-[11.5px] text-slate-500 mt-0.5">No reason was recorded — check the audit trail below.</span>}
          </p>
        </div>
      )}

      {/* Context strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="demo-context-strip">
        {[
          { l: 'Trainer', v: d.trainer_name, i: GraduationCap },
          { l: 'Counsellor', v: d.counsellor_name, i: UserCheck },
          { l: 'Created by', v: `${d.created_by_name || '—'} (${d.created_by_role || '—'})`, i: ShieldCheck },
          { l: 'Approved by', v: d.approved_by_name || 'Pending', i: CheckCircle2 },
        ].map((x) => (
          <div key={x.l} className="rounded-xl border bg-white p-3.5 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><x.i className="w-3 h-3" />{x.l}</p>
            <p className="text-[13px] font-semibold text-slate-800 mt-1 truncate">{x.v}</p>
          </div>
        ))}
      </div>
      {(d.lead_number || d.lead_email || d.lead_phone) && (
        <Card className="p-4 border flex flex-wrap items-center gap-x-5 gap-y-1.5" data-testid="demo-lead-strip">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">CRM Lead</span>
          {d.lead_number ? <span className="text-[12px] font-mono font-semibold text-violet-700">#{d.lead_number}</span> : null}
          {d.student_name ? <span className="text-[12.5px] font-semibold text-slate-800">{d.student_name}</span> : null}
          {d.lead_email ? <span className="text-[11.5px] text-slate-600">{d.lead_email}</span> : null}
          {d.lead_phone ? <span className="text-[11.5px] text-slate-600">{d.lead_phone}</span> : null}
          {d.lead_source ? <span className="text-[10.5px] uppercase tracking-wider text-slate-400">{d.lead_source}</span> : null}
        </Card>
      )}

      {(d.notes || d.rejection_reason) && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-[12.5px] space-y-1.5" data-testid="demo-notes-block">
          {d.notes && <p className="text-slate-600"><span className="text-slate-400 font-semibold uppercase text-[10px] tracking-wider mr-2">Notes</span>{d.notes}</p>}
          {d.rejection_reason && <p className="text-rose-600"><span className="text-rose-400 font-semibold uppercase text-[10px] tracking-wider mr-2">Rejected</span>{d.rejection_reason}</p>}
        </div>
      )}

      {/* Approval — trainer↔manager stage only */}
      {d.status === 'pending_approval' && role === 'trainer' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 flex items-start gap-2.5" data-testid="trainer-awaiting-approval">
          <Clock3 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-amber-900">This demo is <b>waiting for your Training Manager's approval</b>. You'll be notified once it is approved — feedback opens after that.</p>
        </div>
      )}
      {d.status === 'pending_approval' && mgr && !readOnly && (
        <SectionCard icon={ShieldCheck} title="Approve this trainer-created demo" accent="violet" testid="approval-panel">
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Rejection reason (required to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="h-10 flex-1 min-w-[220px] bg-white" data-testid="reject-reason" />
            <Button disabled={busy} onClick={() => act(() => api.post(`/training/demos/${demoId}/approve`), 'Demo approved')} className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" />Approve</Button>
            <Button variant="outline" disabled={busy || !rejectReason.trim()} onClick={() => act(() => api.post(`/training/demos/${demoId}/reject`, { reason: rejectReason }), 'Demo rejected')} className="h-10 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="reject-btn"><XCircle className="w-4 h-4 mr-1.5" />Reject</Button>
          </div>
        </SectionCard>
      )}

      {/* Mark not done / NA */}
      {['pending_approval', 'approved'].includes(d.status) && !locked && !readOnly && (mgr || d.created_by_id === user?.id) && (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="text-[11.5px] text-slate-500" disabled={busy} onClick={() => { setMarkOpen('not_done'); setMarkCode(''); setMarkNote(''); }} data-testid="mark-not-done">Demo Not Conducted</Button>
          <Button size="sm" variant="ghost" className="text-[11.5px] text-slate-500" disabled={busy} onClick={() => { setMarkOpen('not_applicable'); setMarkCode(''); setMarkNote(''); }} data-testid="mark-na">Mark Not Applicable</Button>
        </div>
      )}

      <Dialog open={!!markOpen} onOpenChange={(o) => { if (!o) setMarkOpen(''); }}>
        <DialogContent className="max-w-md bg-white" data-testid="mark-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{markOpen === 'not_applicable' ? 'Mark this demo not applicable' : 'Demo not conducted'}</DialogTitle>
            <DialogDescription className="text-[11.5px] text-slate-500">
              Pick a reason — your name, role and the reason are stored on the demo so everyone can see why it never happened.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Reason<span className="text-rose-500 ml-0.5">*</span></Label>
              <Select value={markCode} onValueChange={setMarkCode}>
                <SelectTrigger className="h-10 bg-white text-[13px]" data-testid="mark-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent className="bg-white">{NOT_DONE_REASONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Note{markCode === 'Other' ? <span className="text-rose-500 ml-0.5">*</span> : <span className="text-slate-400 font-normal normal-case ml-1">(optional)</span>}</Label>
              <Textarea value={markNote} onChange={(e) => setMarkNote(e.target.value)} rows={2}
                placeholder="Anything the team should know" className="bg-white text-[13px]" data-testid="mark-reason-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkOpen('')} data-testid="mark-cancel">Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="mark-confirm"
              disabled={busy || !markCode || (markCode === 'Other' && markNote.trim().length < 5)}
              onClick={() => act(async () => {
                await api.post(`/training/demos/${demoId}/mark`, { status: markOpen, reason_code: markCode, note: markNote });
                setMarkOpen(''); setMarkCode(''); setMarkNote('');
              }, markOpen === 'not_applicable' ? 'Marked not applicable' : 'Marked not conducted')}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRAINER-FOCUSED VIEW */}
      {trainerFocus && approved && (
        <>
          <SectionCard icon={GraduationCap} title="Your Feedback" accent="violet" testid="trainer-feedback-panel"
            right={d.trainer_feedback ? <SubmittedTag at={d.trainer_feedback.submitted_at} onEdit={canTrainerFb && !editing.tf ? () => setEditing((p) => ({ ...p, tf: true })) : undefined} testid="edit-trainer-feedback" /> : null}>
            {showTfForm
              ? <FeedbackForm schema={TRAINER_FB} state={tf} setState={setTf} busy={busy} accent="violet" testPrefix="tf"
                  onSave={() => act(() => api.post(`/training/demos/${demoId}/trainer-feedback`, tf), 'Your feedback is saved')} />
              : <FeedbackReadOnly schema={TRAINER_FB} data={d.trainer_feedback} role="Trainer" accent="violet" />}
          </SectionCard>
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 flex items-start gap-2.5" data-testid="trainer-hidden-notice">
            <EyeOff className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-slate-500">The counsellor's feedback, the alignment comparison and the final outcome stay hidden until this demo is <b>finalised</b>. You'll get a notification the moment that happens.</p>
          </div>
        </>
      )}

      {/* FULL VIEW */}
      {!trainerFocus && approved && (
        <div className="grid lg:grid-cols-2 gap-4">
          <SectionCard icon={GraduationCap} title="Trainer Feedback" accent="violet" testid="trainer-feedback-panel"
            right={d.trainer_feedback ? <SubmittedTag at={d.trainer_feedback.submitted_at} onEdit={canTrainerFb && !editing.tf ? () => setEditing((p) => ({ ...p, tf: true })) : undefined} testid="edit-trainer-feedback" /> : <span className="text-[11px] text-slate-400">Awaiting</span>}>
            {showTfForm
              ? <FeedbackForm schema={TRAINER_FB} state={tf} setState={setTf} busy={busy} accent="violet" testPrefix="tf"
                  onSave={() => act(() => api.post(`/training/demos/${demoId}/trainer-feedback`, tf), 'Trainer feedback saved')} />
              : <FeedbackReadOnly schema={TRAINER_FB} data={d.trainer_feedback} role="Trainer" accent="violet" />}
          </SectionCard>
          <SectionCard icon={UserCheck} title="Counsellor Feedback" accent="indigo" testid="counsellor-feedback-panel"
            right={d.counsellor_feedback ? <SubmittedTag at={d.counsellor_feedback.submitted_at} onEdit={canCounsellorFb && !editing.cf ? () => setEditing((p) => ({ ...p, cf: true })) : undefined} testid="edit-counsellor-feedback" /> : <span className="text-[11px] text-slate-400">Awaiting</span>}>
            {showCfForm
              ? <FeedbackForm schema={COUNSELLOR_FB} state={cf} setState={setCf} busy={busy} accent="indigo" testPrefix="cf"
                  onSave={() => act(() => api.post(`/training/demos/${demoId}/counsellor-feedback`, cf), 'Counsellor feedback saved')} />
              : <FeedbackReadOnly schema={COUNSELLOR_FB} data={d.counsellor_feedback} role="Counsellor" accent="indigo" />}
          </SectionCard>
        </div>
      )}

      {/* Alignment + AI verdict */}
      {!trainerFocus && align.ready && (
        <div className="space-y-4">
          <AiVerdictBanner
            verdict={d.ai_verdict}
            overall={d.feedback_alignment}
            busy={busy}
            onRegenerate={mgr && !readOnly ? () => act(() => api.post(`/training/demos/${demoId}/regenerate-verdict`), 'Verdict regenerated') : undefined}
          />
          {!d.ai_verdict?.text && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-[12.5px] text-slate-500 flex items-center justify-between gap-3" data-testid="ai-verdict-pending">
              <span>AI verdict not generated yet.</span>
              {mgr && !readOnly && <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post(`/training/demos/${demoId}/regenerate-verdict`), 'Verdict generated')} data-testid="generate-verdict-btn">Generate now</Button>}
            </div>
          )}
          <SectionCard icon={Scale} title="Trainer vs Counsellor" accent="slate" testid="alignment-panel"
            right={d.feedback_alignment ? <span className={ALIGN_META[d.feedback_alignment]?.badge}>{ALIGN_META[d.feedback_alignment]?.dot} {ALIGN_META[d.feedback_alignment]?.label}</span> : null}>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead><tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 rounded-l-lg">Aspect</th><th className="px-3 py-2">Trainer</th><th className="px-3 py-2">Counsellor</th><th className="px-3 py-2 rounded-r-lg">Verdict</th>
                </tr></thead>
                <tbody>
                  {align.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`align-row-${i}`}>
                      <td className="px-3 py-2.5 font-semibold text-slate-700">{r.label}</td>
                      <td className="px-3 py-2.5 text-slate-600">{r.trainer || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{r.counsellor || '—'}</td>
                      <td className="px-3 py-2.5">{r.verdict ? <span className={ALIGN_META[r.verdict]?.badge}>{ALIGN_META[r.verdict]?.dot} {ALIGN_META[r.verdict]?.label}</span> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Comments shown separately per role — never merged */}
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              {d.trainer_feedback?.comments && <QuoteBlock text={d.trainer_feedback.comments} author={d.trainer_feedback.submitted_by} role="Trainer" at={d.trainer_feedback.submitted_at} tone="violet" testid="align-trainer-comment" />}
              {d.counsellor_feedback?.comments && <QuoteBlock text={d.counsellor_feedback.comments} author={d.counsellor_feedback.submitted_by} role="Counsellor" at={d.counsellor_feedback.submitted_at} tone="indigo" testid="align-counsellor-comment" />}
            </div>
          </SectionCard>
        </div>
      )}

      {/* Outcome */}
      {!trainerFocus && approved && (
        <SectionCard icon={Target} title="Final Outcome" accent="slate" testid="outcome-panel"
          right={d.outcome ? (
            <span className="inline-flex items-center gap-2">
              <span className={OUTCOME_META[d.outcome.result]?.cls}>{OUTCOME_META[d.outcome.result]?.label}</span>
              {canOutcome && !editing.oc && <EditBtn onClick={() => setEditing((p) => ({ ...p, oc: true }))} testid="edit-outcome" />}
            </span>
          ) : <span className="text-[11px] text-slate-400">Pending</span>}>
          {showOcForm ? (
            <div className="space-y-3">
              <Select value={outcome.result} onValueChange={(v) => setOutcome((p) => ({ ...p, result: v }))}>
                <SelectTrigger className="h-10 bg-white text-[13px]" data-testid="outcome-result"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admission">Admission</SelectItem>
                  <SelectItem value="no_admission">No Admission</SelectItem>
                  <SelectItem value="follow_up">Follow-up Required</SelectItem>
                </SelectContent>
              </Select>
              {outcome.result === 'admission' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 space-y-1.5" data-testid="admission-enrollment-block">
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                    Enrolment Number{mgr ? <span className="text-emerald-600 font-medium normal-case tracking-normal ml-1">(optional for managers — leave blank if not issued yet)</span> : <span className="text-rose-500 ml-0.5">*</span>}
                  </Label>
                  <Input
                    value={outcome.enrollment_no || ''}
                    onChange={(e) => setOutcome((p) => ({ ...p, enrollment_no: e.target.value }))}
                    placeholder="e.g. 1234567"
                    className="h-10 bg-white font-mono"
                    data-testid="outcome-enrollment"
                  />
                  <p className="text-[11px] text-emerald-700">Enter it as issued by CRM/Books. If the student hasn't synced into the Students master yet we keep it as <b>awaiting link</b> and attach it automatically once it lands. The demo <b>locks itself</b> the moment the enrolment number links to a real student.</p>
                </div>
              )}
              {outcome.result === 'no_admission' && (
                <div className="grid sm:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="no-admission-reasons">
                  {NO_ADMISSION_REASONS.map((r) => (
                    <label key={r} className="flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
                      <Checkbox checked={(outcome.reasons || []).includes(r)} onCheckedChange={() => setOutcome((p) => ({ ...p, reasons: (p.reasons || []).includes(r) ? p.reasons.filter((x) => x !== r) : [...(p.reasons || []), r] }))} />{r}
                    </label>
                  ))}
                </div>
              )}
              {outcome.result === 'follow_up' && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Follow-up Date</Label><Input type="date" value={outcome.follow_up_date || ''} onChange={(e) => setOutcome((p) => ({ ...p, follow_up_date: e.target.value }))} className="h-10 mt-1 bg-white" data-testid="followup-date" /></div>
                  <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</Label><Input value={outcome.follow_up_status || ''} onChange={(e) => setOutcome((p) => ({ ...p, follow_up_status: e.target.value }))} className="h-10 mt-1 bg-white" placeholder="e.g. Pending" data-testid="followup-status" /></div>
                </div>
              )}
              <Textarea rows={2} placeholder="Outcome comments" value={outcome.comments || ''} onChange={(e) => setOutcome((p) => ({ ...p, comments: e.target.value }))} className="text-[13px] bg-white" data-testid="outcome-comments" />
              <Button
                disabled={busy || !outcome.result || (outcome.result === 'admission' && !mgr && !(outcome.enrollment_no || '').trim())}
                onClick={() => act(() => api.post(`/training/demos/${demoId}/outcome`, outcome), 'Outcome saved')}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-outcome">Save Outcome</Button>
            </div>
          ) : d.outcome ? (
            <div className="space-y-2 text-[12.5px] text-slate-600">
              {d.outcome.result === 'admission' ? (
                d.outcome.student_id ? (
                  <a href={`/training/students?focus=${d.outcome.enrollment_no}`} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 hover:bg-emerald-100 transition-colors" data-testid="outcome-student-link">
                    <GraduationCap className="w-3.5 h-3.5 text-emerald-700" />
                    <span className="text-[12px] font-semibold text-emerald-900">{d.outcome.student_name || 'Student'}</span>
                    <span className="text-[11px] font-mono text-emerald-700">#{d.outcome.enrollment_no}</span>
                    <span className="text-[10px] uppercase tracking-wider text-emerald-600">Open student record</span>
                  </a>
                ) : (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="outcome-link-pending">
                    <Clock3 className="w-3.5 h-3.5 text-amber-700" />
                    {d.outcome.enrollment_no
                      ? <><span className="text-[11px] font-mono text-amber-900">#{d.outcome.enrollment_no}</span><span className="text-[11px] text-amber-800">awaiting CRM/Books sync — links automatically</span></>
                      : <span className="text-[11px] text-amber-800">Enrolment number awaited</span>}
                  </div>
                )
              ) : null}
              {d.outcome.reasons?.length ? <p><span className="text-slate-400 uppercase text-[10px] font-bold tracking-wider mr-2">Reasons</span>{d.outcome.reasons.join(', ')}</p> : null}
              {d.outcome.follow_up_date ? <p><span className="text-slate-400 uppercase text-[10px] font-bold tracking-wider mr-2">Follow-up</span>{fmtDate(d.outcome.follow_up_date)} ({d.outcome.follow_up_status || '—'})</p> : null}
              {d.outcome.comments ? <QuoteBlock text={d.outcome.comments} author={d.outcome.submitted_by} role="Outcome" at={d.outcome.submitted_at} tone="indigo" testid="outcome-comment-block" /> : null}
            </div>
          ) : <p className="text-[12.5px] text-slate-400">Pending — the counsellor records the final outcome.</p>}
        </SectionCard>
      )}

      {/* Finalise */}
      {!locked && !readOnly && approved && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <p className="text-[12.5px] text-slate-500">{canFinalise ? 'Both feedbacks and the outcome are in — finalise to lock this record.' : 'Finalise unlocks once both feedbacks and the final outcome are recorded.'}</p>
          <Button disabled={!canFinalise || busy} onClick={() => act(() => api.post(`/training/demos/${demoId}/finalise`), 'Demo finalised')} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="finalise-btn">
            <Lock className="w-4 h-4 mr-1.5" />Finalise Demo
          </Button>
        </div>
      )}

      {/* Discussion */}
      <DemoComments demoId={demoId} />

      {/* Audit trail */}
      {!trainerFocus && (
        <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="audit-trail">
          <summary className="cursor-pointer text-[12.5px] font-semibold text-slate-600 flex items-center gap-2"><History className="w-3.5 h-3.5" />Audit trail ({(d.audit || []).length})</summary>
          <ul className="mt-3 space-y-1.5">
            {(d.audit || []).map((a, i) => (
              <li key={i} className="text-[11.5px] text-slate-500 border-l-2 border-slate-200 pl-3">
                {new Date(a.at).toLocaleString('en-IN')} — <b className="text-slate-700">{a.action}</b> by {a.by_name} ({a.by_role}){a.detail ? ` · ${a.detail}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Edit demo details — admin / manager */}
      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="max-w-2xl bg-white" data-testid="edit-demo-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Edit demo details — {d.demo_code}</DialogTitle>
            <DialogDescription className="text-[11px] text-slate-500">
              Fill in anything that was missed. Every change is written to the audit trail{locked ? ' — this demo is finalised, edits still apply.' : '.'}
            </DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-4 py-1">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Trainer</Label>
                <SearchSelect value={edit.trainer_id} onChange={(v) => setEdit((p) => ({ ...p, trainer_id: v }))} options={masters.trainers} placeholder="Select trainer" testid="edit-trainer" className="w-full" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Counsellor</Label>
                <SearchSelect value={edit.counsellor_id} onChange={(v) => setEdit((p) => ({ ...p, counsellor_id: v }))} options={masters.counsellors} placeholder="Select counsellor" testid="edit-counsellor" className="w-full" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Course</Label>
                <SearchSelect value={edit.course_id} onChange={(v) => setEdit((p) => ({ ...p, course_id: v }))} options={masters.courses} placeholder="Select course" testid="edit-course" className="w-full" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Student / Prospect</Label>
                <Input value={edit.student_name} onChange={(e) => setEdit((p) => ({ ...p, student_name: e.target.value }))} className="h-10 bg-white" data-testid="edit-student" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Date</Label>
                <Input type="date" value={edit.demo_date} onChange={(e) => setEdit((p) => ({ ...p, demo_date: e.target.value }))} className="h-10 bg-white" data-testid="edit-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Time</Label>
                <Input type="time" value={edit.demo_time} onChange={(e) => setEdit((p) => ({ ...p, demo_time: e.target.value }))} className="h-10 bg-white" data-testid="edit-time" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Demo Type</Label>
                <Select value={edit.demo_type} onValueChange={(v) => setEdit((p) => ({ ...p, demo_type: v }))}>
                  <SelectTrigger className="h-10 bg-white text-[13px]" data-testid="edit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-white">{DEMO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                <Textarea rows={2} value={edit.notes} onChange={(e) => setEdit((p) => ({ ...p, notes: e.target.value }))} className="bg-white text-[13px]" data-testid="edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="edit-demo-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="edit-demo-save">
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DemoDetailPage() {
  const { demoId } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <DemoDetailBody
      demoId={demoId}
      forcedRo={sp.get('ro') === '1'}
      onClose={() => navigate(demoBase(user?.role))}
    />
  );
}
